export type VrFormat = "2d" | "180" | "180-stereo" | "360" | "360-stereo";

export type ProbeVideoRole = "MainVideo" | "Trailer";

export type VideoProbeMetadata = {
  durationSeconds: number;
  width: number;
  height: number;
  aspectRatio: number;
  fileExtension: string;
  fileSizeMB: number;
};

export type CompatibilityStatus = "compatible" | "warning" | "incompatible";

export type VideoProbeValidation = {
  status: CompatibilityStatus;
  /** Blocking problems: the file should not be uploaded as-is. */
  errors: string[];
  /** Non-blocking hints about the selected VR format. */
  warnings: string[];
  /** Human readable projection that the measured aspect ratio looks like. */
  detectedProjection: string | null;
};

export type VideoProbeResult = {
  /** False when the browser could not decode the container/codec. */
  probed: boolean;
  metadata: VideoProbeMetadata;
  validation: VideoProbeValidation;
};

export const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".mov", ".mkv", ".webm"];

export const MAX_FILE_SIZE_MB: Record<ProbeVideoRole, number> = {
  MainVideo: 40 * 1024,
  Trailer: 2 * 1024,
};

export const MIN_VIDEO_WIDTH = 640;
export const MIN_VIDEO_HEIGHT = 360;

const RECOMMENDED_MIN_WIDTH: Record<VrFormat, number> = {
  "2d": 1920,
  "180": 3840,
  "180-stereo": 3840,
  "360": 3840,
  "360-stereo": 3840,
};

const ASPECT_TOLERANCE = 0.06;
const PROBE_TIMEOUT_MS = 20000;

type Projection = { label: string; ratio: number };

const P_16_9: Projection = { label: "16:9 flat", ratio: 16 / 9 };
const P_1_1: Projection = { label: "1:1 (square / top-bottom)", ratio: 1 };
const P_2_1: Projection = {
  label: "2:1 (equirectangular / side-by-side)",
  ratio: 2,
};
const P_4_1: Projection = { label: "4:1 (side-by-side 360)", ratio: 4 };

const EXPECTED_PROJECTIONS: Record<VrFormat, Projection[]> = {
  "2d": [P_16_9, { label: "4:3 flat", ratio: 4 / 3 }, { label: "21:9 flat", ratio: 21 / 9 }],
  "180": [P_1_1, P_2_1, P_16_9],
  "180-stereo": [P_2_1, P_1_1],
  "360": [P_2_1],
  "360-stereo": [P_1_1, P_4_1],
};

const ALL_PROJECTIONS: Projection[] = [
  P_2_1,
  P_1_1,
  P_16_9,
  P_4_1,
  { label: "4:3 flat", ratio: 4 / 3 },
  { label: "21:9 flat", ratio: 21 / 9 },
];

export const VR_FORMATS: VrFormat[] = [
  "2d",
  "180",
  "180-stereo",
  "360",
  "360-stereo",
];

export function isVrFormat(value: unknown): value is VrFormat {
  return typeof value === "string" && (VR_FORMATS as string[]).includes(value);
}

export function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  if (idx <= 0 || idx === fileName.length - 1) return "";
  return fileName.slice(idx).toLowerCase();
}

export function bytesToMB(bytes: number): number {
  return Math.round((bytes / (1024 * 1024)) * 100) / 100;
}

function matches(ratio: number, target: number): boolean {
  return Math.abs(ratio - target) / target <= ASPECT_TOLERANCE;
}

export function detectProjection(aspectRatio: number): string | null {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return null;
  const hit = ALL_PROJECTIONS.find((p) => matches(aspectRatio, p.ratio));
  return hit ? hit.label : null;
}

export function formatAspectRatio(aspectRatio: number): string {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return "Unknown";
  const named = ALL_PROJECTIONS.find((p) => matches(aspectRatio, p.ratio));
  if (named) {
    const [label] = named.label.split(" ");
    return `${label} (${aspectRatio.toFixed(2)})`;
  }
  return aspectRatio.toFixed(2) + ":1";
}

/**
 * Pure validation of probed metadata against the selected VR format.
 * `format` may be empty/unknown (the creator has not picked one yet), in which
 * case only format independent checks run.
 */
export function validateVideoMetadata(
  metadata: VideoProbeMetadata,
  format: string | null | undefined,
  role: ProbeVideoRole = "MainVideo"
): VideoProbeValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  const { durationSeconds, width, height, aspectRatio, fileExtension } =
    metadata;

  if (!ALLOWED_VIDEO_EXTENSIONS.includes(fileExtension)) {
    errors.push(
      `Unsupported file extension "${fileExtension || "none"}". Allowed: ${ALLOWED_VIDEO_EXTENSIONS.join(", ")}.`
    );
  }

  if (!(metadata.fileSizeMB > 0)) {
    errors.push("File is empty.");
  } else if (metadata.fileSizeMB > MAX_FILE_SIZE_MB[role]) {
    const limitGB = MAX_FILE_SIZE_MB[role] / 1024;
    errors.push(
      `File is ${(metadata.fileSizeMB / 1024).toFixed(2)} GB which exceeds the ${limitGB} GB limit for ${role === "MainVideo" ? "main videos" : "trailers"}.`
    );
  }

  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    errors.push("Video duration could not be determined or is zero.");
  }

  const hasDimensions = width > 0 && height > 0;
  if (!hasDimensions) {
    errors.push("Video resolution could not be determined.");
  } else if (width < MIN_VIDEO_WIDTH || height < MIN_VIDEO_HEIGHT) {
    errors.push(
      `Resolution ${width}x${height} is below the minimum of ${MIN_VIDEO_WIDTH}x${MIN_VIDEO_HEIGHT}.`
    );
  }

  const detectedProjection = hasDimensions ? detectProjection(aspectRatio) : null;

  if (isVrFormat(format) && hasDimensions) {
    const recommendedWidth = RECOMMENDED_MIN_WIDTH[format];
    if (
      width >= MIN_VIDEO_WIDTH &&
      height >= MIN_VIDEO_HEIGHT &&
      width < recommendedWidth
    ) {
      warnings.push(
        format === "2d"
          ? `Resolution ${width}x${height} is below the recommended ${recommendedWidth}px width (Full HD).`
          : `Resolution ${width}x${height} is low for ${format} VR content; at least ${recommendedWidth}px width (4K) is recommended.`
      );
    }

    const expected = EXPECTED_PROJECTIONS[format];
    if (!expected.some((p) => matches(aspectRatio, p.ratio))) {
      const wanted = expected.map((p) => p.label).join(" or ");
      const looksLike = detectedProjection
        ? ` The footage looks like ${detectedProjection}.`
        : "";
      warnings.push(
        `Aspect ratio ${aspectRatio.toFixed(2)}:1 does not match the typical ${wanted} layout for "${format}" content.${looksLike}`
      );
    }
  }

  const status: CompatibilityStatus =
    errors.length > 0
      ? "incompatible"
      : warnings.length > 0
      ? "warning"
      : "compatible";

  return { status, errors, warnings, detectedProjection };
}

export function buildFileMetadata(file: {
  name: string;
  size: number;
}): Pick<VideoProbeMetadata, "fileExtension" | "fileSizeMB"> {
  return {
    fileExtension: getFileExtension(file.name),
    fileSizeMB: bytesToMB(file.size),
  };
}

/**
 * Reads duration and resolution using a temporary, detached HTMLVideoElement.
 * Rejects if the browser cannot decode the file (e.g. an unsupported codec).
 */
export function probeVideoFile(
  file: File,
  timeoutMs: number = PROBE_TIMEOUT_MS
): Promise<VideoProbeMetadata> {
  return new Promise<VideoProbeMetadata>((resolve, reject) => {
    if (typeof document === "undefined" || typeof URL === "undefined") {
      reject(new Error("Video probing is not supported in this environment."));
      return;
    }

    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      try {
        video.load();
      } catch {
        // ignore: jsdom / detached element
      }
      URL.revokeObjectURL(url);
    };

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    video.preload = "metadata";
    video.muted = true;

    video.onloadedmetadata = () => {
      const width = video.videoWidth || 0;
      const height = video.videoHeight || 0;
      const durationSeconds = Number.isFinite(video.duration)
        ? video.duration
        : 0;
      finish(() =>
        resolve({
          durationSeconds,
          width,
          height,
          aspectRatio: height > 0 ? width / height : 0,
          ...buildFileMetadata(file),
        })
      );
    };

    video.onerror = () =>
      finish(() =>
        reject(new Error("The browser could not read this video file."))
      );

    timer = setTimeout(
      () =>
        finish(() =>
          reject(new Error("Timed out while reading video metadata."))
        ),
      timeoutMs
    );

    video.src = url;
  });
}

/**
 * Probes `file` and validates it for the selected VR `format`. Never rejects:
 * if the browser cannot decode the video, only file level checks (extension,
 * size) are enforced and the result is marked `probed: false`.
 */
export async function inspectVideoFile(
  file: File,
  format: string | null | undefined,
  role: ProbeVideoRole = "MainVideo"
): Promise<VideoProbeResult> {
  try {
    const metadata = await probeVideoFile(file);
    return {
      probed: true,
      metadata,
      validation: validateVideoMetadata(metadata, format, role),
    };
  } catch (err) {
    const metadata: VideoProbeMetadata = {
      durationSeconds: 0,
      width: 0,
      height: 0,
      aspectRatio: 0,
      ...buildFileMetadata(file),
    };
    const full = validateVideoMetadata(metadata, format, role);
    const errors = full.errors.filter(
      (e) =>
        !e.startsWith("Video duration") && !e.startsWith("Video resolution")
    );
    const message =
      err instanceof Error ? err.message : "The browser could not read this video.";
    const warnings = [
      `${message} Resolution and duration will be verified by the server after upload.`,
    ];
    return {
      probed: false,
      metadata,
      validation: {
        status: errors.length > 0 ? "incompatible" : "warning",
        errors,
        warnings,
        detectedProjection: null,
      },
    };
  }
}
